import type { Endpoint, EndpointPickSource } from './endpoint';

/** One endpoint's slot in the pen page state — enough to restore both the input and the pin. */
export interface PenEndpointSlot {
  /** The picked endpoint, or `undefined` for an empty slot. */
  endpoint: Endpoint | undefined;
  /** Displayed input text — mirrors picks / map clicks / raw typing. */
  text: string;
  /** Source coord of the pick, used for stop-kind pins. */
  source: EndpointPickSource | undefined;
}

/** Everything the pen page needs to serialise into a shareable URL. */
export interface PenUrlState {
  origin: PenEndpointSlot;
  destination: PenEndpointSlot;
}

/** Empty-slot factory — hands out a fresh instance so callers can freely mutate the return value. */
export function emptySlot(): PenEndpointSlot {
  return { endpoint: undefined, text: '', source: undefined };
}

/** Writes one slot's fields into `parameters` under the `origin_*` or `dest_*` prefix. */
function writeSlot(
  parameters: URLSearchParams,
  prefix: 'origin' | 'dest',
  slot: PenEndpointSlot,
): void {
  const endpoint = slot.endpoint;
  if (!endpoint) return;
  writeEndpoint(parameters, prefix, endpoint);
  writeSourceIfNeeded(parameters, prefix, endpoint, slot.source);
  if (slot.text) parameters.set(`${prefix}_label`, slot.text);
}

/** Encodes the endpoint's kind + payload into `parameters`. */
function writeEndpoint(parameters: URLSearchParams, prefix: string, endpoint: Endpoint): void {
  if (endpoint.kind === 'position') {
    parameters.set(`${prefix}_lat`, String(endpoint.latitude));
    parameters.set(`${prefix}_lon`, String(endpoint.longitude));
    return;
  }
  if (endpoint.kind === 'stop') {
    parameters.set(`${prefix}_stop`, String(endpoint.stopId));
    return;
  }
  parameters.set(`${prefix}_addr`, endpoint.address);
}

/** Adds the source coord for stop endpoints; other kinds derive it from the endpoint itself. */
function writeSourceIfNeeded(
  parameters: URLSearchParams,
  prefix: string,
  endpoint: Endpoint,
  source: EndpointPickSource | undefined,
): void {
  if (!source || endpoint.kind !== 'stop') return;
  parameters.set(`${prefix}_lat`, String(source.latitude));
  parameters.set(`${prefix}_lon`, String(source.longitude));
}

/** Reads one slot out of the given prefix; empty when the parameters carry no endpoint fields. */
function readSlot(parameters: URLSearchParams, prefix: 'origin' | 'dest'): PenEndpointSlot {
  const endpoint = readEndpoint(parameters, prefix);
  if (!endpoint) return emptySlot();
  const source = readSource(parameters, prefix, endpoint);
  const text = parameters.get(`${prefix}_label`) ?? '';
  return { endpoint, text, source };
}

/** Picks the endpoint variant that has all required fields present. Undefined when none does. */
function readEndpoint(parameters: URLSearchParams, prefix: string): Endpoint | undefined {
  const stopIdText = parameters.get(`${prefix}_stop`);
  if (stopIdText) return readStopEndpoint(stopIdText);
  const address = parameters.get(`${prefix}_addr`);
  if (address) return { kind: 'address', address };
  return readPositionEndpoint(parameters, prefix);
}

/** Parses the stop-id string. Returns `undefined` for a non-integer value. */
function readStopEndpoint(stopIdText: string): Endpoint | undefined {
  const stopId = Number(stopIdText);
  if (!Number.isSafeInteger(stopId)) return undefined;
  return { kind: 'stop', stopId };
}

/** Reads `{prefix}_lat` + `{prefix}_lon` as finite floats and returns a position endpoint. */
function readPositionEndpoint(parameters: URLSearchParams, prefix: string): Endpoint | undefined {
  const latText = parameters.get(`${prefix}_lat`);
  const lonText = parameters.get(`${prefix}_lon`);
  if (latText === null || lonText === null) return undefined;
  const latitude = Number(latText);
  const longitude = Number(lonText);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  return { kind: 'position', latitude, longitude };
}

/** Reads the pin source for stop endpoints; other kinds return undefined (they derive from endpoint). */
function readSource(
  parameters: URLSearchParams,
  prefix: string,
  endpoint: Endpoint,
): EndpointPickSource | undefined {
  if (endpoint.kind !== 'stop') return undefined;
  const latText = parameters.get(`${prefix}_lat`);
  const lonText = parameters.get(`${prefix}_lon`);
  if (latText === null || lonText === null) return undefined;
  const latitude = Number(latText);
  const longitude = Number(lonText);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  return { latitude, longitude };
}

/** Serialises the full pen state into `?origin_...&dest_...` — the URL search string. */
export function encodePenUrl(state: PenUrlState): string {
  const parameters = new URLSearchParams();
  writeSlot(parameters, 'origin', state.origin);
  writeSlot(parameters, 'dest', state.destination);
  const encoded = parameters.toString();
  return encoded ? `?${encoded}` : '';
}

/** Parses a `?origin_...&dest_...` search string back into a `PenUrlState`. */
export function decodePenUrl(search: string): PenUrlState {
  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  const parameters = new URLSearchParams(trimmed);
  return { origin: readSlot(parameters, 'origin'), destination: readSlot(parameters, 'dest') };
}
